import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-05-27 15:23:24 +0200 (Wed, 27 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163322 $
//  Purpose   : XMenu
// =============================================================================
// ---Third-party imports
import React, { useRef, createContext, useContext, useMemo } from 'react';
import { isFragment, isElement } from 'react-is';
import { Menu, Icon, Dropdown, Button, Input, Popup, Header, Modal, Label } from 'semantic-ui-react';
import clsx from 'clsx';
// ---Framework imports
import { isNil, isObject, isBoolean, isArray, isString, isFunction, isNumber, callIfFunc, dict, keys, has, mapItems, size as ldash_size } from 'ldash';
import log from 'uix/logging';
import { useDebounce, useEffect, useState, useSelector, useIsOffline, useActions, useCallback, usePersistentWithQueryOverride, useFullscreenContext, useResettableState, usePartContext } from 'uix/hooks';
import { VIcon, Icons, vicon } from 'uix/graphics';
import { Sel, Actions } from 'uix/registries';
import { checkAcl } from 'uix/auth/acl';
import { isValid, parse } from 'uix/utils/date';
// ---Local imports
import styles from './menu.css';
import { AutoPortal } from './portals';
import { applyProps } from './utils';
import { KeyboardShortCut } from './keyboardShortcuts';
import { DatePicker } from './datepicker';
import { ResizableDialog } from './dialog';
import { Link } from './links';
import { XPopup } from './popup';
import { SearchableJSONTree } from 'uix/chunks';
import { DebugFeature } from './debug';
// ---Globals
const PPrintButton = ({ data, defaultQuery = '', expand, icon = 'bug', content, title, open, keepOpen, header, position, compact = false, popupStyle, as = 'dialog' })=>{
    let [expanded, setExpanded] = useState(false);
    let [query, setQuery] = useState(defaultQuery);
    if (expand) {
        expanded = expand;
    }
    let shouldExpandNode = expanded ? ()=>true : undefined;
    let treeStyle = {};
    if (compact) {
        treeStyle = {
            padding: '0.2em',
            margin: '0.5em',
            lineHeight: '0.8em',
            fontSize: '0.7em'
        };
    }
    let theme = {
        tree: ({ style, className })=>{
            style = {
                ...style,
                ...treeStyle
            };
            return {
                style,
                className
            };
        },
        nestedNodeLabel: ({ style }, keyPath, nodeType, expanded)=>({
                style
            })
    };
    let jsontree = /*#__PURE__*/ _jsx(SearchableJSONTree, {
        hideRoot: true,
        data: data,
        query: query,
        shouldExpandNode: shouldExpandNode,
        theme: theme
    });
    let itemProps = {
        icon,
        content,
        title
    };
    if (as == 'dialog') {
        return /*#__PURE__*/ _jsx(DialogItem, {
            ...itemProps,
            dlgHeader: header || title,
            children: /*#__PURE__*/ _jsxs("div", {
                style: {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                },
                children: [
                    /*#__PURE__*/ _jsxs(XMenu, {
                        attached: true,
                        size: "small",
                        style: {
                            width: '99%'
                        },
                        children: [
                            /*#__PURE__*/ _jsx(XMenu.Toggle, {
                                value: expanded,
                                set: setExpanded,
                                content: "Expand"
                            }),
                            /*#__PURE__*/ _jsx(XMenu.Input, {
                                icon: "filter",
                                debounce: 300,
                                value: query,
                                onValueChange: setQuery
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            flex: 1,
                            overflow: 'auto',
                            lineHeight: '1rem',
                            fontSize: '0.9rem',
                            backgroundColor: 'rgb(0, 43, 54)'
                        },
                        children: jsontree
                    })
                ]
            })
        });
    } else if (as == 'popup') {
        return /*#__PURE__*/ _jsxs(XPopupItem, {
            ...itemProps,
            open: open,
            keepOpen: keepOpen,
            basic: true,
            position: position,
            popupStyle: popupStyle,
            children: [
                /*#__PURE__*/ _jsx(Button, {
                    style: {
                        position: 'absolute',
                        right: '0px',
                        zIndex: 100,
                        marginTop: '-5px'
                    },
                    secondary: true,
                    compact: true,
                    size: "mini",
                    icon: "expand",
                    active: expanded,
                    onClick: ()=>setExpanded(!expanded)
                }),
                jsontree
            ]
        });
    } else {
        log.error('invalid mode: %s', as);
        return null;
    }
};
const DebugButton = ({ portalTarget, open, publicVisible = false, onAction, ...props })=>{
    let button = /*#__PURE__*/ _jsx(PPrintButton, {
        as: "popup",
        keepOpen: true,
        open: open,
        compact: true,
        ...props,
        popupStyle: {
            minWidth: '70px',
            maxHeight: '70vh',
            overflow: 'auto'
        }
    });
    if (onAction) {
        button = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                button,
                /*#__PURE__*/ _jsx(XItem, {
                    icon: [
                        'bug',
                        {
                            name: 'play',
                            color: 'red'
                        }
                    ],
                    onClick: onAction,
                    title: "Run debug action"
                })
            ]
        });
    }
    if (portalTarget) {
        button = /*#__PURE__*/ _jsx(AutoPortal, {
            target: portalTarget,
            children: button
        });
    }
    if (!publicVisible) {
        button = /*#__PURE__*/ _jsx(DebugFeature, {
            children: button
        });
    }
    return button;
};
let XMenu = ({ size = null, padded = false, className = null, id, ref, children, borderless, ...props })=>{
    /* menu that supports semantics ui style*/ //~ children = applyProps(children, {mode})
    return /*#__PURE__*/ _jsx(Menu, {
        ref: ref,
        className: clsx(padded && styles.padded, className, borderless && styles.borderless),
        size: size,
        ...props,
        children: children
    });
};
let buildConfirmation = ({ confirmSpec, confirmButton = 'OK', confirmationOpen, setConfirmationOpen, onConfirmed, i18n })=>{
    let onConfirmClicked = ()=>{
        onConfirmed();
        setConfirmationOpen(false);
    };
    //~ log.x(confirm)
    let contentConfirm = isBoolean(confirmSpec) ? undefined : confirmSpec;
    let modal_options = contentConfirm?.modalOptions || {};
    let disabled = false;
    let buildContent = (content)=>{
        if (isFunction(content)) {
            return content();
        }
        return content;
    };
    let header = null;
    let onConfirmationOpen = null;
    let onConfirmationClose = null;
    let cancelButton = i18n.get('abort');
    if (isElement(contentConfirm) || isFragment(contentConfirm)) {
        // raw react
        contentConfirm = /*#__PURE__*/ _jsx("div", {
            className: "content",
            children: buildContent(contentConfirm)
        });
    } else if (isObject(contentConfirm)) {
        // options: confirmButton, onOpen, onClose
        let { content, buttonDisabled, header: h, size } = contentConfirm;
        header = h;
        if (has(contentConfirm, 'confirmButton')) {
            // pass through null for explicit disable
            confirmButton = contentConfirm.confirmButton;
        }
        if (contentConfirm.cancelButton) {
            cancelButton = contentConfirm.cancelButton;
        }
        modal_options = {
            ...modal_options,
            size
        };
        disabled = !!buttonDisabled;
        contentConfirm = /*#__PURE__*/ _jsx("div", {
            className: "content",
            children: buildContent(content)
        });
        // handle onOpen + onClose
        onConfirmationOpen = contentConfirm.onOpen;
        onConfirmationClose = contentConfirm.onClose;
    }
    useEffect(()=>{
        if (!isNil(confirmationOpen)) {
            if (onConfirmationOpen && confirmationOpen) onConfirmationOpen();
            if (onConfirmationClose && !confirmationOpen) onConfirmationClose();
        }
    }, [
        confirmationOpen
    ]);
    return /*#__PURE__*/ _jsxs(Modal, {
        size: "small",
        open: confirmationOpen,
        ...modal_options,
        children: [
            /*#__PURE__*/ _jsx(KeyboardShortCut, {
                name: "Escape",
                onTrigger: (e)=>setConfirmationOpen(false)
            }),
            /*#__PURE__*/ _jsx(KeyboardShortCut, {
                name: "Enter",
                onTrigger: (e)=>disabled ? null : onConfirmClicked()
            }),
            modal_options.header && /*#__PURE__*/ _jsx(Modal.Header, {
                content: modal_options.header
            }),
            header && /*#__PURE__*/ _jsx(Modal.Header, {
                children: header
            }),
            /*#__PURE__*/ _jsx(Modal.Content, {
                content: contentConfirm
            }),
            /*#__PURE__*/ _jsxs(Modal.Actions, {
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        secondary: true,
                        onClick: ()=>setConfirmationOpen(false),
                        children: cancelButton
                    }),
                    confirmButton && /*#__PURE__*/ _jsx(Button, {
                        primary: true,
                        disabled: disabled,
                        content: confirmButton,
                        onClick: onConfirmClicked
                    })
                ]
            })
        ]
    });
};
let XItem = ({ content, icon, className, disabled, active, children, confirm, cornerContent, shortcut, acl, prominent = false, ...props })=>{
    let { i18n, user, dname } = usePartContext();
    let isOffline = useIsOffline();
    disabled = disabled || isOffline;
    let confirmation = null;
    let [confirmationOpen, setConfirmationOpen] = useState(false);
    if (confirm) {
        let { onClick, ...otherProps } = props;
        props = {
            ...props,
            onClick: ()=>setConfirmationOpen(true)
        };
        confirmation = /*#__PURE__*/ React.createElement(buildConfirmation, {
            confirmSpec: confirm,
            confirmationOpen,
            setConfirmationOpen,
            onConfirmed: onClick,
            i18n
        });
    }
    if (acl && !checkAcl(acl, user, dname)) {
        return null;
    }
    if (cornerContent) {
        content = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx("span", {
                    style: {
                        position: 'absolute',
                        fontSize: '0.8em',
                        top: 1,
                        right: 1
                    },
                    children: cornerContent
                }),
                content
            ]
        });
    }
    if (isString(icon) && Icons[icon]) {
        icon = vicon(icon);
    } else if (isObject(icon) && Icons[icon.name]) {
        let { name, ...icon_props } = icon;
        icon = vicon(name, icon_props);
    } else if (isArray(icon)) {
        let [icon1, icon2, icon3] = icon;
        let decode = (i)=>isString(i) ? {
                name: i,
                disabled
            } : {
                ...i,
                disabled
            };
        children = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs(Icon.Group, {
                    children: [
                        /*#__PURE__*/ _jsx(VIcon, {
                            ...decode(icon1)
                        }),
                        icon2 && /*#__PURE__*/ _jsx(VIcon, {
                            corner: "bottom right",
                            ...decode(icon2)
                        }),
                        icon3 && /*#__PURE__*/ _jsx(VIcon, {
                            corner: "bottom left",
                            ...decode(icon3)
                        })
                    ]
                }),
                (content || children) && /*#__PURE__*/ _jsxs("span", {
                    children: [
                        children,
                        content
                    ]
                })
            ]
        });
        icon = undefined;
        content = undefined; // pushed into children
    }
    let kbdshortcut = null;
    if (shortcut) {
        kbdshortcut = /*#__PURE__*/ _jsx(KeyboardShortCut, {
            name: shortcut,
            onTrigger: props.onClick
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Menu.Item, {
                className: clsx(prominent && styles.prominent, className, !children && !content && styles.iconOnly),
                content: content,
                icon: icon,
                disabled: disabled,
                active: active,
                ...props,
                children: children
            }),
            confirmation,
            kbdshortcut
        ]
    });
};
const SuiSep = ({ style, className })=>/*#__PURE__*/ _jsx("div", {
        style: style,
        className: clsx(styles.separator, className)
    });
const useDebouncedInputValue = ({ value, defaultValue, debounce, debounceLeading, onValueChange })=>{
    // track modified text (reset if external input changes)
    // also if debounced onValueChange -> value rountrip
    let [activeValue, setActiveValue] = useResettableState(null, [
        value
    ]);
    let [debounced] = useDebounce(activeValue, parseInt(debounce) || 0, {
        leading: debounceLeading
    });
    useEffect(()=>{
        if (onValueChange && debounced !== null) {
            onValueChange(debounced);
        }
    }, [
        debounced
    ]);
    if (activeValue === null) {
        if (!isNil(value)) {
            activeValue = value;
        } else if (defaultValue) {
            activeValue = defaultValue;
        }
    }
    return {
        activeValue,
        setActiveValue
    };
};
let XInput = ({ defaultValue, value, width, maxWidth, fluid, fitted, onValueChange, onEnterKey, debounce, debounceLeading, autoFocus, selectOnFocus = false, icon, validator, onFocus, ...props })=>{
    let { activeValue, setActiveValue } = useDebouncedInputValue({
        value,
        defaultValue,
        debounce,
        debounceLeading,
        onValueChange
    });
    let outerStyle = fluid ? {
        flex: 1
    } : {};
    let style = fluid ? {
        flex: 1
    } : {};
    if (width) {
        style = {
            ...style,
            width: `${width}px`
        };
    }
    if (maxWidth) {
        style = {
            ...style,
            maxWidth: `${maxWidth}px`
        };
    }
    if (fitted) {
        style = {
            ...style,
            padding: 0,
            border: '0px'
        };
        outerStyle = {
            ...outerStyle,
            paddingRight: '2px',
            paddingLeft: '2px'
        };
    }
    let handleChange = (event, data)=>{
        if (validator && !new RegExp(validator).test(data.value)) {
            return; // don't update state if validation fails
        }
        setActiveValue(data.value);
    };
    let handleFocus = (event)=>{
        // If searchAspect is true, select the input's text
        if (selectOnFocus) {
            event.target.select();
        }
        callIfFunc(onFocus, event);
    };
    return /*#__PURE__*/ _jsx(XItem, {
        fitted: true,
        style: outerStyle,
        children: /*#__PURE__*/ _jsx(Input, {
            icon: icon,
            input: /*#__PURE__*/ _jsx("input", {
                style: style
            }),
            autoFocus: autoFocus,
            value: activeValue ?? '',
            onChange: handleChange,
            onKeyDown: (event)=>event.key == 'Enter' ? callIfFunc(onEnterKey, activeValue, event) : null,
            fluid: fluid,
            onFocus: handleFocus,
            ...props
        })
    });
};
const FilterInput = ({ debounce = 500, placeholder = 'Filtern...', ...props })=>{
    return /*#__PURE__*/ _jsx(XInput, {
        icon: "filter",
        placeholder: placeholder,
        debounce: debounce,
        ...props
    });
};
let XSliderInput = ({ min = 0, max = 10, value, defaultValue, onValueChange, width, debounce, debounceLeading, disabled = false, content, icon, valueLabeled = false, defaultOnIconClick = false, ...props })=>{
    let { activeValue, setActiveValue } = useDebouncedInputValue({
        value,
        defaultValue,
        debounce,
        debounceLeading,
        onValueChange
    });
    let icon_props = {};
    if (defaultOnIconClick && isNumber(defaultValue)) {
        icon_props = {
            onClick: ()=>setActiveValue(defaultValue),
            style: {
                cursor: 'pointer'
            }
        };
    }
    return /*#__PURE__*/ _jsxs(Menu.Item, {
        ...props,
        children: [
            icon && /*#__PURE__*/ _jsx(VIcon, {
                name: icon,
                ...icon_props
            }),
            valueLabeled && /*#__PURE__*/ _jsx("span", {
                style: {
                    position: 'absolute',
                    fontSize: '0.5em',
                    bottom: 0,
                    left: '40%'
                },
                children: activeValue
            }),
            /*#__PURE__*/ _jsx("input", {
                name: "cheese",
                type: "range",
                min: min,
                max: max,
                value: activeValue,
                disabled: disabled,
                onChange: (event)=>setActiveValue(parseInt(event.target.value)),
                readOnly: onValueChange ? false : true,
                style: width ? {
                    width
                } : undefined
            })
        ]
    });
};
// Context that lets child XPopupItems signal to a parent (e.g. Toolbar.More) that they are open,
// so the parent can keep itself open while a sticky child is active.
export const ChildPopupRegistry = /*#__PURE__*/ createContext(null);
let XPopupItem = ({ open, children, wide, keepOpen = false, closeOnChildClick = false, basic = false, hideOnScroll, flowing, padded, position, positionFixed, onOpen, onClose, onCloseAttempt, onClick, popupStyle, renderAlways = false, closeOnPopupDescendantClick = true, ...props })=>{
    // if external open is touched, reset internal state
    let [isOpen, setOpen] = useResettableState(false, [
        open
    ]);
    if (isNil(open)) {
        open = isOpen;
    }
    // Register with parent registry (e.g. Toolbar.More) when open,
    // so the parent knows to stay open while this sticky child is active.
    // Also provides a close callback so the parent can force-close us (e.g. when More closes by trigger).
    const registry = useContext(ChildPopupRegistry);
    const registryRef = useRef(registry);
    registryRef.current = registry;
    // Keep a stable ref to onClose so the close callback always calls the latest version
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useEffect(()=>{
        if (!registryRef.current) return;
        if (open) {
            const closeFn = ()=>{
                setOpen(false);
                callIfFunc(onCloseRef.current);
            };
            registryRef.current.register(closeFn);
            return ()=>registryRef.current?.unregister(closeFn);
        }
    }, [
        open
    ]);
    let onClickOpen = useCallback(()=>{
        setOpen(!open);
        callIfFunc(onOpen);
        callIfFunc(onClick);
    });
    let onPopupClose = useCallback((event)=>{
        const hasPopupAncestor = (el)=>!!el && !!el.closest('.popup');
        if (hasPopupAncestor(event.target)) {
            // if click-event stems from element that has popup as ancestor -> keep this popup open
            // i.e. clicking within content of a popup that is child of another popup (e.g. in toolbar -> more -> popupItem)
            // note: doesn't check whether this popup is actual trigger-item that opens parent-context of event.target
            if (!closeOnPopupDescendantClick) return;
        }
        // Always fire onCloseAttempt so callers can react (e.g. show "unsaved changes" warning)
        // regardless of whether keepOpen blocks the actual close.
        callIfFunc(onCloseAttempt);
        if (!keepOpen) {
            setOpen(false);
            callIfFunc(onClose);
        }
    });
    let handleChildClick = (event)=>{
        if (closeOnChildClick) {
            setOpen(false);
            callIfFunc(onClose);
        }
    };
    return /*#__PURE__*/ _jsx(XPopup, {
        on: "click",
        open: renderAlways || open,
        basic: basic,
        flowing: flowing,
        hideOnScroll: hideOnScroll,
        wide: wide,
        className: clsx(padded && styles.paddedPopup, styles.popup, renderAlways && !open && styles.closed),
        position: position,
        onClose: (event)=>open ? onPopupClose(event) : undefined,
        trigger: /*#__PURE__*/ _jsx(XItem, {
            ...props,
            active: open || props.active,
            onClick: onClickOpen
        }),
        style: popupStyle,
        children: /*#__PURE__*/ _jsx("div", {
            onClick: handleChildClick,
            children: children
        })
    });
};
let CopyToClipboardItem = ({ text, name, showName = true, ...props })=>{
    let successNotification = useActions(Actions.successNotification);
    let copy = ()=>{
        navigator.clipboard.writeText(text);
        successNotification({
            message: `${text.length} Zeichen kopiert.`
        });
    };
    return /*#__PURE__*/ _jsxs(XMenu.Item, {
        onClick: copy,
        title: name ?? 'Copy to Clipboard',
        ...props,
        children: [
            /*#__PURE__*/ _jsx(VIcon, {
                name: "copy"
            }),
            showName ? name ?? 'Copy to Clipboard' : null
        ]
    });
};
let DialogItem = ({ dlg, children, dlgMode = 'resizable', name = 'unnamedDlg', dlgIcon = null, dlgClose = true, dlgHeader = null, dlgModalSize = 'small', dlgModalActions = null, dlgStoreKey = undefined, fullscreenButton, open, closeDep, onClose, ...props })=>{
    let [dialogOpen, setDialogOpen] = useResettableState(false, [
        closeDep
    ]);
    if (!dlg) {
        // use children as dlg content
        dlg = children;
    } else {
        // use children for menu item
        props = {
            ...props,
            children
        };
    }
    if (!isNil(open)) {
        dialogOpen = open;
    }
    let dialog = null;
    let close = ()=>isFunction(onClose) ? onClose() : setDialogOpen(false);
    let escapeKey = /*#__PURE__*/ _jsx(KeyboardShortCut, {
        name: "Escape",
        onTrigger: close
    });
    let dialog_node = null;
    let { domNode } = useFullscreenContext();
    if (dlgMode == 'modal') {
        dialog_node = /*#__PURE__*/ _jsxs(Modal, {
            className: styles.modal,
            size: dlgModalSize == 'normal' ? undefined : dlgModalSize,
            open: dialogOpen,
            mountNode: domNode || undefined,
            dimmer: ()=>'blurring',
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.header,
                    children: [
                        /*#__PURE__*/ _jsx(Modal.Header, {
                            as: "h2",
                            children: dlgHeader
                        }),
                        dlgClose && /*#__PURE__*/ _jsx(Button, {
                            icon: true,
                            secondary: true,
                            onClick: ()=>close(),
                            className: styles.headerButton,
                            children: /*#__PURE__*/ _jsx(VIcon, {
                                name: "close"
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Modal.Content, {
                    content: dlg
                }),
                dlgModalActions && /*#__PURE__*/ _jsx(Modal.Actions, {
                    children: dlgModalActions
                }),
                escapeKey
            ]
        });
    } else if (dlgMode == 'resizable' && dialogOpen) {
        dialog_node = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                escapeKey,
                /*#__PURE__*/ _jsx(ResizableDialog, {
                    name: name,
                    onClose: close,
                    icon: dlgIcon,
                    header: dlgHeader,
                    children: dlg,
                    storeKey: dlgStoreKey,
                    fullscreenButton: fullscreenButton
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(XItem, {
                onClick: ()=>setDialogOpen(!dialogOpen),
                ...props,
                active: dialogOpen
            }),
            dialog_node
        ]
    });
};
const LinkItem = ({ // props of menu item
children, active, icon, content, disabled, // props for <Link/>
...props })=>{
    /* build a <Link/> and simulate a sui menu item via css classes
   */ if (isObject(icon)) {
        icon = /*#__PURE__*/ _jsx(VIcon, {
            ...icon
        });
    } else if (isString(icon)) {
        icon = /*#__PURE__*/ _jsx(VIcon, {
            name: icon
        });
    } else {
        icon = null;
    }
    children = children || /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            icon,
            content
        ]
    });
    return /*#__PURE__*/ _jsx(Link, {
        ...props,
        disabled: disabled,
        className: clsx('item', active && 'active', disabled && 'disabled', !content && styles.iconOnly),
        children: children
    });
};
const CallApiItem = ({ method, params, onCalled, children, ...props })=>{
    /* combine a XItem with callApi for given method and params
   */ let callApi = useActions(Actions.callApi);
    let onClick = ()=>{
        if (method) {
            callApi(method, params || {}, onCalled);
        } else {
            log.warn('no api method given');
        }
    };
    return /*#__PURE__*/ _jsx(XItem, {
        onClick: onClick,
        ...props,
        children: children
    });
};
const ButtonGroupItem = ({ active, onChange, buttons, multi = false })=>{
    if (isString(buttons[0])) {
        buttons = buttons.map((b)=>({
                name: b,
                content: b
            }));
    }
    let [_active, _setActive] = useState(dict(buttons.map((b)=>[
            b.name,
            false
        ])));
    if (active) {
        if (multi) {
            _active = active;
        } else {
            _active = {
                [active]: true
            };
        }
    }
    let setActive = (name, value)=>{
        let next_active = _active;
        if (multi) {
            next_active = {
                ...next_active,
                [name]: value
            };
        } else {
            next_active = {
                [name]: value
            };
        }
        _setActive(next_active);
        onChange(multi ? next_active : name);
    };
    return buttons.map(({ name, children, ...props })=>/*#__PURE__*/ _jsx(XItem, {
            active: !!_active[name],
            onClick: ()=>setActive(name, !_active[name]),
            ...props,
            children: children
        }, name));
};
const LogChannelItem = ({ channel, ...props })=>{
    let toggleLogStream = useActions('toggleLogStream');
    return /*#__PURE__*/ _jsx(XMenu.Item, {
        onClick: ()=>toggleLogStream({
                channel: 'vls.scheduler'
            }),
        icon: "binoculars",
        ...props
    });
};
// ---Exports
XMenu.BeginRight = ({ ...props })=>/*#__PURE__*/ _jsx(XItem, {
        fitted: true,
        position: "right",
        ...props
    });
XMenu.Dropdown = ({ ...props })=>/*#__PURE__*/ _jsx(XDropdown, {
        ...props
    });
XMenu.Input = XInput;
XMenu.Slider = XSliderInput;
XMenu.Item = XItem;
XMenu.FilterInput = FilterInput;
XMenu.PopupItem = XPopupItem;
XMenu.PPrintButton = PPrintButton;
XMenu.DebugButton = DebugButton; // PPrintButton in popup form
XMenu.CopyToClipboardItem = CopyToClipboardItem;
XMenu.DialogItem = DialogItem;
XMenu.LinkItem = LinkItem;
XMenu.ButtonGroupItem = ButtonGroupItem;
XMenu.CallApiItem = CallApiItem;
XMenu.LogChannelItem = LogChannelItem;
// displaynames needed by toolbar.js
XPopupItem.displayName = 'XPopupItem';
XMenu.Label = ({ ...props })=>/*#__PURE__*/ _jsx(XItem, {
        style: {
            fontWeight: 'bold'
        },
        ...props
    });
XMenu.Separator = ({ ...props })=>/*#__PURE__*/ _jsx(SuiSep, {
        ...props
    });
XMenu.Toggle = ({ value, set, invert = false, ...props })=>/*#__PURE__*/ _jsx(XItem, {
        active: invert ? !value : !!value,
        onClick: ()=>set(!value),
        ...props
    });
XMenu.ExternalLink = ({ href, ...props })=>{
    let apiurl = useSelector(Sel.apiurl);
    return /*#__PURE__*/ _jsx(XItem, {
        onClick: ()=>window.open(apiurl.href(href)),
        ...props
    });
};
XMenu.DownloadLink = ({ href, ...props })=>{
    let apiurl = useSelector(Sel.apiurl);
    return /*#__PURE__*/ _jsx(XItem, {
        children: /*#__PURE__*/ _jsx("a", {
            download: true,
            href: apiurl.href(href),
            children: /*#__PURE__*/ _jsx(Icon, {
                fitted: true,
                name: "download"
            })
        })
    });
};
XMenu.SubMenu = ({ children, hideOnScroll = true, keepOpen = false, padded = false, ...props })=>{
    /* combines a Popup and a vertical Menu
   */ let [open, setOpen] = useState(false);
    const INTERNAL_ONLY_KEYS = [
        'data-element',
        'data-source-file'
    ];
    let onChildClick = (orgOnClick)=>(...args)=>{
            if (!keepOpen) {
                setOpen(false);
            }
            return orgOnClick(...args);
        };
    let intercepted = [
        'onClick',
        'set'
    ];
    children = applyProps(children, (props)=>{
        let publicKeys = keys(props).filter((name)=>!INTERNAL_ONLY_KEYS.includes(name));
        if (publicKeys.length == 0) return props // hack for <Separator/>, what about <Label>?
        ;
        let candidates = intercepted.filter((name)=>props[name]);
        if (candidates.length) {
            let name = candidates[0];
            return {
                ...props,
                [name]: onChildClick(props[name])
            };
        } else {
            // fragile .Dropdown detection (handle onSetValue?)
            let is_ok = !!props.onSetValue || !!props.onValueChange || keepOpen;
            if (!is_ok) {
                log.warn('we need to intercept a onClick on child items for closing submenu popup (on %o) (or set keepOpen)', publicKeys);
            }
        }
    });
    return /*#__PURE__*/ _jsx(XPopupItem, {
        padded: padded,
        basic: true,
        hideOnScroll: hideOnScroll,
        keepOpen: keepOpen,
        open: open,
        onOpen: ()=>setOpen(true),
        onClose: ()=>setOpen(false),
        ...props,
        children: /*#__PURE__*/ _jsx(XMenu, {
            padded: padded,
            vertical: true,
            size: "large",
            children: children
        })
    });
};
XMenu.DatePicker = ({ submitOnQuickSelect = false, minDate = '2010', ...props })=>{
    /* complex daterange + granularity picker
   */ //~ let [opts, setOpts] = useState({granularity: undefined, startDate: minDate, endDate: null})
    return /*#__PURE__*/ _jsx(DatePicker, {
        submitOnQuickSelect: submitOnQuickSelect,
        minDate: minDate,
        ...props
    });
};
XMenu.DateInput = ({ onValueChange, onChange, value, icon, emitInvalid = false, debounce, debounceLeading, ...props })=>{
    /* simple native date picker
  emitInvalid: emit empty/invalid dates during editing (with keyboard)
   */ let [_value, _setValue] = useState(null);
    let _onChange = useCallback((e)=>{
        _setValue(e.target.value);
        callIfFunc(onChange, e);
    });
    let [debounced] = useDebounce(_value, parseInt(debounce) || 0, {
        leading: debounceLeading
    });
    useEffect(()=>{
        if (debounced === null) return; // during init
        if (emitInvalid || isValid(parse(debounced, 'yyyy-MM-dd', new Date()))) {
            callIfFunc(onValueChange, debounced);
        }
    }, [
        debounced
    ]);
    if (isString(icon)) {
        icon = {
            name: icon
        };
    }
    if (_value !== null) {
        value = _value;
    }
    return /*#__PURE__*/ _jsxs(XMenu.Item, {
        children: [
            icon && /*#__PURE__*/ _jsx(Icon, {
                ...icon
            }),
            /*#__PURE__*/ _jsx("input", {
                type: "date",
                value: value,
                onChange: _onChange
            })
        ]
    });
};
export { XMenu };
export const XDropdown = ({ iconOnly = false, labeled = false, children = null, attached = false, onChange, // XXX: rename to onValueChange
onSetValue, onValueChange, disabled, options, trigger, minWidth, style, ...props })=>{
    let isOffline = useIsOffline();
    disabled = disabled || isOffline;
    let labeledProps = null;
    labeled ? labeledProps = {
        labeled: true,
        compact: true
    } : null;
    // XXX: remove onSetValue
    if (onValueChange || onSetValue) {
        onChange = useCallback((e, data)=>onValueChange ? onValueChange(data.value) : onSetValue(data.value), [
            onValueChange,
            onSetValue
        ]);
    }
    if (options && isString(options[0])) {
        // transform simple strings in compatible option datastructure
        options = options.map((v)=>({
                text: v,
                value: v
            }));
    }
    // if trigger is a function, evaluate with active values
    if (isFunction(trigger)) {
        // assume controlled component
        let value = props.value;
        let text = options?.find((opt)=>opt.value == value)?.text;
        trigger = trigger({
            value,
            text
        });
    }
    let dropdownStyle = style;
    if (minWidth) {
        dropdownStyle = {
            ...style,
            minWidth: `${minWidth}px`
        };
    }
    return /*#__PURE__*/ _jsx(Dropdown, {
        item: true,
        onChange: onChange,
        className: clsx(styles.dropdown, attached && styles.attached, iconOnly && styles.iconOnly, labeled && clsx(styles.labeled, 'icon')),
        disabled: disabled,
        options: options?.map(({ icon, ...opt })=>({
                ...opt,
                content: icon ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(VIcon, {
                            name: icon,
                            style: {
                                marginRight: '0.5rem'
                            }
                        }),
                        opt.text
                    ]
                }) : undefined
            })),
        trigger: trigger,
        style: dropdownStyle,
        ...props,
        ...labeledProps,
        children: children
    });
};
export const Confirmation = ({ open, options, onClose, content })=>{
    let i18n = useSelector(Sel.i18n);
    let runConfirmed = ()=>{
        options.onConfirm();
        onClose();
    };
    if (isNil(content)) {
        content = options?.content;
    }
    let confirmation = /*#__PURE__*/ React.createElement(buildConfirmation, {
        confirmSpec: content,
        confirmationOpen: open,
        setConfirmationOpen: onClose,
        onConfirmed: runConfirmed,
        i18n
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: confirmation
    });
};
export const DDMenu = ({ icon, text, children, ddsymbol = true, ...props })=>{
    if (!ddsymbol) {
        props.icon = null;
    }
    if (icon) {
        props.trigger = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Icon, {
                    name: icon
                }),
                text
            ]
        });
    } else if (text) {
        props.text = text;
    }
    return /*#__PURE__*/ _jsx(Dropdown, {
        ...props,
        children: /*#__PURE__*/ _jsx(Dropdown.Menu, {
            children: children
        })
    });
};
DDMenu.Item = Dropdown.Item;
export const ConfirmButton = ({ // ConfirmationButton
confirm, children, ...props })=>{
    /* A Button with confirmation support
   */ let i18n = useSelector(Sel.i18n);
    let confirmation = null;
    let [confirmationOpen, setConfirmationOpen] = useState(false);
    if (confirm) {
        let { onClick, ...otherProps } = props;
        props = {
            ...props,
            onClick: ()=>setConfirmationOpen(true)
        };
        confirmation = /*#__PURE__*/ React.createElement(buildConfirmation, {
            confirmSpec: confirm,
            confirmationOpen,
            setConfirmationOpen,
            onConfirmed: onClick,
            i18n
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Button, {
                ...props,
                children: children
            }),
            confirmation
        ]
    });
};
export const ProgressMenuItem = ({ value = 20, total = 80, suffix, ...props })=>{
    return /*#__PURE__*/ _jsxs(Menu.Item, {
        fitted: true,
        ...props,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    paddingLeft: '1em',
                    paddingRight: '1em'
                },
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        children: value !== total ? `${value}/${total}` : String(value)
                    }),
                    suffix && /*#__PURE__*/ _jsx("span", {
                        children: suffix
                    })
                ]
            }),
            value > 0 && value < total && /*#__PURE__*/ _jsx("div", {
                style: {
                    position: 'absolute',
                    backgroundColor: 'teal',
                    width: `${value / total * 100}%`,
                    height: '60%',
                    opacity: 0.2,
                    lineHeight: '1.5rem'
                }
            })
        ]
    });
};
export const GlobalMenu = ({ children, ...props })=>{
    return /*#__PURE__*/ _jsx(AutoPortal, {
        target: "TabMenuPortal",
        children: /*#__PURE__*/ _jsx(Menu, {
            borderless: true,
            ...props,
            children: children
        })
    });
};
const usePersistentItemData = ({ name, menuProps, initialAsDefault = false })=>{
    let { initialValue, version = 1, ...props } = menuProps;
    let [value, setValue] = usePersistentWithQueryOverride({
        name,
        initialValue,
        replace: true
    }, version);
    if (initialAsDefault) {
        props = {
            ...props,
            defaultValue: initialValue
        };
    }
    if (!props.content && !props.icon && !props.children) {
        // default content (is a bad idea for items that spawn childen automaticly)
        props = {
            ...props,
            content: name
        };
    }
    return [
        value,
        setValue,
        props
    ];
};
export const usePersistentToggle = (name, menuProps = {})=>{
    /* useToggle with persistence
   */ if (!isString(name)) {
        if (isObject(name) && !menuProps) {
            menuProps = name;
        }
        log.warn('unnamed persitent item');
        name = 'unnamed_toggle';
        menuProps.content = name;
    }
    let [value, setValue, props] = usePersistentItemData({
        name,
        menuProps
    });
    value = value || false; // initial empty string to false
    return [
        value,
        /*#__PURE__*/ _jsx(XMenu.Toggle, {
            value: value,
            set: setValue,
            ...props
        }, name)
    ];
};
export const useToggle = (name, menuProps = {})=>{
    /* useToggle without persistence
   */ let { initialValue = false, ...props } = menuProps;
    let [value, _setValue] = useState(initialValue);
    let setValue = useCallback(()=>_setValue(!value), [
        value
    ]);
    return [
        value,
        /*#__PURE__*/ _jsx(XMenu.Toggle, {
            value: value,
            set: setValue,
            ...props
        }, name)
    ];
};
export const usePersistentButtonGroup = (name, menuProps = {})=>{
    /* */ let [value, setValue, props] = usePersistentItemData({
        name,
        menuProps
    });
    value = value || menuProps.initialValue; // XXX: why is this not default?
    return [
        value,
        /*#__PURE__*/ _jsx(XMenu.ButtonGroupItem, {
            active: value,
            onChange: setValue,
            ...props
        }, name)
    ];
};
export const usePersistentDropdown = (name, menuProps = {})=>{
    /*
  options in menuProps [{text, value}]
   */ let [value, setValue, props] = usePersistentItemData({
        name,
        menuProps
    });
    return [
        value,
        /*#__PURE__*/ _jsx(XMenu.Dropdown, {
            value: value,
            onSetValue: setValue,
            ...props
        })
    ];
};
export const usePersistentSlider = (name, menuProps = {})=>{
    /* options in menuProps [{text, value}]
   */ let [value, setValue, props] = usePersistentItemData({
        name,
        menuProps,
        initialAsDefault: true
    });
    return [
        value,
        /*#__PURE__*/ _jsx(XMenu.Slider, {
            value: value,
            onValueChange: setValue,
            ...props
        }, name)
    ];
};
export const usePersistentInput = (name, menuProps = {})=>{
    /* useToggle
  options in menuProps [{text, value}]
   */ let [value, setValue, props] = usePersistentItemData({
        name,
        menuProps
    });
    // hmm, url param decoding on otid param gives us ints here
    value = String(value);
    return [
        value,
        /*#__PURE__*/ _jsx(XInput, {
            value: value,
            onValueChange: setValue,
            ...props
        }, name)
    ];
};
const buildPersistentItem = ([value, item])=>({
        value,
        item
    });
export const usePersistentItems = ({ toggle, dropdown, slider })=>dict([
        ...mapItems(toggle, (name, props)=>[
                name,
                buildPersistentItem(usePersistentToggle(name, props))
            ]),
        ...mapItems(dropdown, (name, props)=>[
                name,
                buildPersistentItem(usePersistentDropdown(name, props))
            ]),
        ...mapItems(slider, (name, props)=>[
                name,
                buildPersistentItem(usePersistentSlider(name, props))
            ])
    ]);
